import { useState } from 'react';
import { NJButton, NJText } from '@engie-group/fluid-design-system-react';
import { UMFormStyles, UMSelect, UMR } from '../../urbanmap/_components';
import type { PlanGeoData } from '../proposition-c/types';
import { captureInputs, type PlanInputs } from './construction';
import { FAMILIES, type Plan, type Project } from './types';
import { SetSummary } from './SetSummary';

function InputDetails({ inputs }: { inputs: PlanInputs }) {
  const q = inputs.project.params;
  const rows: [string, string][] = [
    ['Projet', `${inputs.project.name} · ${inputs.project.id}`],
    ['Responsable', inputs.project.owner],
    ['Copie du périmètre', `${inputs.project.perimeterLabel} · ${inputs.project.snapshotDate}`],
    ['Dérive UrbanMap', `${inputs.project.umDriftCount} bâtiments modifiés`],
    ['Plan', `${inputs.plan.name} · ${inputs.plan.id} · ${inputs.plan.energy}`],
    ['Périmètre', q.perimeter], ['Réseau existant', q.existingNetwork], ['Nature du réseau', q.networkKind],
    ['Sélections importées', String(q.selectionsImported)],
    ['Station météo', q.weatherStation], ['DJU', String(q.dju)], ['Températures', q.temperatureProfile],
    ['Ratios chaud', q.heatRatio], ['Ratios froid', q.coldRatio],
    ['Dates', `${q.startDate} → ${q.endDate}`], ['Coefficient de surlongueur', String(q.detourCoef)],
  ];
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
    <NJText as="h4" scale="sm" style={{ margin: 0 }}>Projet et paramètres</NJText>
    {rows.map(([label, value]) => <NJText key={label} scale="xs" style={{ margin: 0 }}>
      <strong>{label} : </strong>{value}
    </NJText>)}
    <NJText as="h4" scale="sm" style={{ margin: 0 }}>Hypothèses : valeurs et versions</NJText>
    {inputs.hypotheses.map(({ family, selectedId, values }) => <section key={family}>
      <NJText scale="sm" style={{ margin: '0 0 8px' }}>
        <strong>{FAMILIES.find((item) => item.key === family)?.title}</strong>
        {values ? ` · ${values.name} · v${values.version} · ${values.updatedAt}` : ` · Jeu manquant (${selectedId})`}
      </NJText>
      {values && <SetSummary set={values} />}
    </section>)}
    <NJText as="h4" scale="sm" style={{ margin: 0 }}>Composition des bâtiments</NJText>
    {inputs.selections.map((source) => <NJText key={source.id} scale="xs" style={{ margin: 0 }}>
      {source.name} · {source.buildingIds.length} bâtiments · copie du {source.updatedAt}
    </NJText>)}
    {inputs.missingSelectionIds.length > 0 && <NJText role="alert" scale="xs">
      Sources indisponibles : {inputs.missingSelectionIds.join(', ')}
    </NJText>}
    <NJText scale="xs" style={{ margin: 0 }}>
      {inputs.addedBuildingIds.length} ajouts individuels · {inputs.excludedBuildingIds.length} retraits · {inputs.buildings.length} bâtiments uniques retenus
    </NJText>
    {inputs.buildings.map(({ id, data }) => <NJText key={id} scale="xs" style={{ margin: 0 }}>
      {data?.address ?? id} · {data?.usage ?? 'Données indisponibles'} · {data?.heatKnown ? `${data.heat.toLocaleString('fr-FR')} MWh/an (chaleur estimée)` : 'Consommation indisponible'}
      {' · '}{inputs.addedBuildingIds.includes(id) ? 'Ajout individuel' : 'Issu des sélections'}
    </NJText>)}
    {inputs.excludedBuildingIds.length > 0 && <NJText scale="xs" style={{ margin: 0 }}>
      Bâtiments retirés : {inputs.excludedBuildingIds.join(', ')}
    </NJText>}
    <NJText as="h4" scale="sm" style={{ margin: 0 }}>Tracé et phasage</NJText>
    <NJText scale="xs" style={{ margin: 0 }}>
      Tracé {inputs.trace.mode === 'manual' ? 'manuel' : 'automatique'} · {inputs.trace.indexes.length} tronçons.
      {' '}Tronçons : {inputs.trace.indexes.map((index) => index + 1).join(', ') || 'Aucun'}.
    </NJText>
    <NJText as="h4" scale="sm" style={{ margin: 0 }}>Locaux techniques et imports</NJText>
    <NJText scale="xs">Les points importés, leurs coordonnées, associations et statuts sont conservés dans l’export, y compris les points à vérifier et l’historique des remplacements.</NJText>
    {(inputs.technicalPoints ?? []).map((point) => <NJText key={point.id} scale="xs" style={{ margin: 0 }}>
      {point.id.slice(-8)} · {point.status === 'validated' ? 'Validé' : point.status === 'replaced' ? 'Remplacé' : 'À vérifier'} · {point.buildingId ?? 'Sans bâtiment'} · {point.coordinates.join(', ')} · {point.fileName}{point.originPlan ? ` · origine : ${point.originPlan}` : ''}
    </NJText>)}
    <NJText scale="xs" style={{ margin: 0 }}>
      {inputs.phasing.mode === 'auto-simulation' ? 'Phasage automatique simulé.' : inputs.phasing.mode === 'mixed' ? 'Phasage mixte : simulation et dates manuelles.' : 'Phasage manuel.'} {inputs.phasing.rule}
    </NJText>
    {Object.entries(inputs.phasing.buildings).map(([id, year]) => <NJText key={id} scale="xs" style={{ margin: 0 }}>
      {inputs.buildings.find((building) => building.id === id)?.data?.address ?? id} → {year}
    </NJText>)}
    {Object.entries(inputs.phasing.pipePhases).map(([index, year]) => <NJText key={index} scale="xs" style={{ margin: 0 }}>
      Tronçon {Number(index) + 1} → {year} · {inputs.phasing.pipeManualOverrides?.[Number(index)] ? 'Manuel' : 'Automatique (mock)'}
    </NJText>)}
    <NJText as="h4" scale="sm" style={{ margin: 0 }}>Données et limites de la simulation</NJText>
    <NJText scale="xs" style={{ margin: 0 }}>
      {inputs.geography?.source ?? 'Géométrie indisponible'} · sous-échantillonnage mock : {inputs.simulation.samplingSkip}.
      Les géométries disponibles sont incluses dans l’export.
    </NJText>
    {inputs.simulation.limitations.map((text) => <NJText key={text} scale="xs" style={{ margin: 0 }}>{text}</NJText>)}
  </div>;
}

export function PlanRecap({ project, plan, geo, dirty, onExport }: {
  project: Project; plan: Plan; geo?: PlanGeoData; dirty: boolean; onExport?: () => void;
}) {
  const [historical, setHistorical] = useState(false);
  const [archiveIndex, setArchiveIndex] = useState('last');
  const archive = archiveIndex === 'last' ? plan.snapshot : plan.archives?.[Number(archiveIndex)]?.snapshot;
  const current = captureInputs(project, plan, geo);
  const inputs = historical ? archive?.inputs : current;
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
    <UMFormStyles />
    <NJText as="h3" scale="md" style={{ margin: 0 }}>Récapitulatif</NJText>
    <NJText scale="xs" style={{ margin: 0, color: UMR.textSecondary }}>
      Tous les paramètres disponibles, et pas seulement les hypothèses. L’export inclut la configuration courante et l’archive du dernier calcul, distinctement.
    </NJText>
    {dirty && <NJText role="status" scale="xs">Configuration courante non confirmée. Confirmez ou annulez avant d’exporter.</NJText>}
    <NJButton label="Ouvrir Import-export · ce plan présélectionné" variant="primary" scale="sm"
      disabled={dirty || plan.status === 'running'} onClick={onExport} />
    {!geo && <NJText scale="xs" role="status">Export en attente de la carte et des géométries.</NJText>}
    <NJText scale="xs">{plan.archives?.length ?? 0} archives exactes disponibles dans l’export projet. Aucun historique reconstitué.</NJText>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      <NJButton label="Configuration courante" scale="sm" variant={!historical ? 'primary' : 'secondary'} onClick={() => setHistorical(false)} />
      <NJButton label="Dernier calcul" scale="sm" variant={historical ? 'primary' : 'secondary'} onClick={() => setHistorical(true)} />
    </div>
    {historical && <UMSelect value={archiveIndex} ariaLabel="Archive de calcul" onChange={setArchiveIndex} options={[
      { value: 'last', label: `Dernier calcul · ${plan.snapshot?.computedAt ?? 'Aucun'}` },
      ...(plan.archives ?? []).map(({ snapshot }, index) => ({ value: String(index), label: `Archive ${index + 1} · ${snapshot.computedAt}` })),
    ]} />}
    {historical && <NJText scale="xs" style={{ margin: 0 }}>
      {archive ? `Calcul du ${archive.computedAt}` : 'Aucun calcul effectué.'}
    </NJText>}
    {historical && archive && !inputs && <NJText role="status" scale="xs">
      Ancien calcul de démonstration : seuls les identifiants et versions des hypothèses ont été archivés.
      Relancez le calcul pour figer l’ensemble des paramètres ; les valeurs actuelles ne sont pas présentées comme historiques.
    </NJText>}
    {inputs && <InputDetails inputs={inputs} />}
  </div>;
}
